'use client';

import { AlertTriangle, Rocket, Upload, XIcon } from 'lucide-react';
import { useRef, useState } from 'react';

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Progress,
} from 'src/components';

import type { UseDataTableImportReturn } from './use-data-table-import';

export type DataTableImporterProps = UseDataTableImportReturn & {
  title?: string;
};

export function DataTableImporter({
  open,
  onOpenChange,
  loading,
  result,
  handleImport,
  handleDownloadTemplate,
  cancelImport,
  reset,
  isSuccess,
  isError,
  /** 组件属性 */
  title = '导入数据',
}: DataTableImporterProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // 处理对话框关闭
  const handleClose = () => {
    if (loading) {
      cancelImport();
    }
    reset();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setSelectedFile(null);
    onOpenChange(false);
  };

  // 点击上传文件按钮
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    fileInputRef.current?.click();
  };

  // 文件选择（只选择文件，不立即上传）
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // 清除之前的错误
      if (isError()) {
        reset();
      }
    }
  };

  // 开始导入
  const handleStartImport = async () => {
    if (selectedFile) {
      await handleImport(selectedFile);
    }
  };

  // 下载模板
  const handleDownloadClick = async () => {
    await handleDownloadTemplate(`${title}模板`);
  };

  // 渲染进度信息
  const renderProgressInfo = () => {
    if (!result) return null;
    return (
      <div className="flex-col gap-1">
        <div className="flex justify-between items-center">
          <div className="text-sm text-muted-foreground">{result.message || '正在处理...'}</div>
          <div className="text-sm text-muted-foreground">{result.progress}%</div>
        </div>
        <Progress value={result.progress} className="h-4" />
      </div>
    );
  };

  // 渲染提示信息
  const renderAlert = () => {
    if (!result) return null;

    // 错误
    if (result.error) {
      return (
        <Alert variant="destructive" className="border-destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{result.error.message || '导入错误！'}</AlertTitle>
          {result.error.details && result.error.details.length > 0 && (
            <AlertDescription>
              <div className="max-h-48 overflow-y-auto space-y-1 w-full">
                {result.error.details.map((error, idx) => (
                  <div key={idx}>{error.message}</div>
                ))}
              </div>
            </AlertDescription>
          )}
        </Alert>
      );
    }

    // 成功
    if (result.success) {
      return (
        <Alert className="border-border">
          <Rocket className="h-4 w-4" />
          <AlertTitle>导入成功！</AlertTitle>
          <AlertDescription>
            {result.data?.count !== undefined && `共导入 ${result.data.count} 条数据`}
          </AlertDescription>
        </Alert>
      );
    }

    return null;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-md"
        showCloseButton={false}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="relative">
          <DialogTitle>{title}</DialogTitle>
          <button
            onClick={handleClose}
            className="absolute top-0 right-0 rounded-xs opacity-70 hover:opacity-100 transition-opacity focus:outline-none"
          >
            <XIcon className="h-4 w-4" />
            <span className="sr-only">关闭</span>
          </button>
          <DialogDescription>
            请上传excel格式文件，
            <Button variant="link" className="p-0 h-auto" onClick={handleDownloadClick}>
              下载文件模板
            </Button>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* 上传区域 */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={handleFileChange}
                disabled={loading}
              />
              <Button
                variant="outline"
                className="h-9"
                onClick={handleUploadClick}
                disabled={loading || isSuccess()}
              >
                <Upload className="mr-2 h-4 w-4" />
                {selectedFile ? '重新选择' : '选择文件'}
              </Button>
              {selectedFile && (
                <span className="text-sm text-muted-foreground break-all">{selectedFile.name}</span>
              )}
            </div>
          </div>

          {/* 进度显示 */}
          {renderProgressInfo()}

          {/* 提示信息 */}
          {renderAlert()}
        </div>

        <DialogFooter className="flex justify-end gap-2">
          {isSuccess() ? (
            <Button type="button" onClick={() => handleClose()}>
              关闭
            </Button>
          ) : (
            <>
              <Button type="button" variant="secondary" onClick={handleClose}>
                取消
              </Button>
              <Button
                type="button"
                onClick={handleStartImport}
                disabled={!selectedFile || loading || isError()}
              >
                {loading ? '导入中...' : '导入'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
